import React, { useEffect, useRef, useState } from 'react';
import { Input, Spin, theme } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { IconCurrentLocation, IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { suggestLocations } from '../services/LocationService';
import { formatLocationLabel, LOCATION_SEARCH_DEBOUNCE_MS, LOCATION_SEARCH_MIN_QUERY_LENGTH } from './LocationAutocomplete';
import { ILocation } from '../interfaces/heavyDutyMachinery/ILocation';
import { CORAL } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface HomeSearchLocationScreenProps {
  initialQuery: string;
  onSelect: (location: ILocation) => void;
  onNearMe: () => void;
  // Fires the moment the query text becomes empty -- typing it out
  // character by character or tapping the input's own clear (X) button
  // both end up here, since antd's allowClear just fires onChange with an
  // empty value like any other edit. Without this, clearing the text only
  // ever cleared what's visibly typed, never the already-committed
  // location the caller was still holding onto -- there was no button to
  // "accept" an empty field, so navigating back re-showed whatever was
  // selected before, with no way to actually end up with no location.
  onClear: () => void;
  locating: boolean;
}

/**
 * The mobile search overlay's "Where" subscreen -- a plain in-page list
 * below the input instead of LocationAutocomplete's own antd AutoComplete
 * popup, which was rendering (options genuinely populated, network request
 * firing) but visually hidden behind this overlay's own zIndex: 2000 --
 * antd's popup portals default to a much lower z-index (~1000, see
 * zIndexPopupBase), so it was there in the DOM, just underneath the opaque
 * overlay. A plain inline list sidesteps that whole class of bug instead of
 * fighting z-index. Reuses suggestLocations (this app's own HeavyDutyMachineryLocation
 * cache, same data source HomeSearchBar's desktop pill already searches)
 * and formatLocationLabel/debounce constants from LocationAutocomplete --
 * not a parallel implementation, just a different rendering shell around
 * the same service call.
 */
const HomeSearchLocationScreen: React.FC<HomeSearchLocationScreenProps> = ({ initialQuery, onSelect, onNearMe, onClear, locating }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<ILocation[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Autofocus the moment this subscreen slides in -- the whole point of a
  // dedicated screen for this field is to get straight to typing.
  useEffect(() => {
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < LOCATION_SEARCH_MIN_QUERY_LENGTH) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await suggestLocations(query));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, LOCATION_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '4px 20px 12px', flexShrink: 0 }}>
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            if (next.trim() === '') onClear();
          }}
          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CITY_PLACEHOLDER)}
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
          allowClear
          size="large"
          style={{ borderRadius: 999 }}
          autoComplete="heavyDutyMachinery-location-search"
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px' }}>
        <button
          type="button"
          onClick={onNearMe}
          disabled={locating}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            padding: '14px 16px',
            marginBottom: 8,
            borderRadius: 16,
            border: 'none',
            background: token.colorFillAlter,
            cursor: locating ? 'default' : 'pointer',
            textAlign: 'left',
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: CORAL,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {locating ? <Spin size="small" style={{ color: '#fff' }} /> : <IconCurrentLocation size={18} />}
          </span>
          <span style={{ fontWeight: 600, fontSize: 15, color: token.colorText }}>
            {translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_NEAR_ME_LABEL)}
          </span>
        </button>

        {searching && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}

        {!searching && query.trim().length >= LOCATION_SEARCH_MIN_QUERY_LENGTH && results.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: token.colorTextTertiary, fontSize: 14 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_NO_RESULTS)}
          </div>
        )}

        {!searching &&
          results.map((location, index) => (
            <button
              key={`${location.locality}__${index}`}
              type="button"
              onClick={() => onSelect(location)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                borderBottom: index < results.length - 1 ? `1px solid ${token.colorBorderSecondary}` : 'none',
              }}
            >
              <IconMapPin size={18} style={{ marginTop: 2, flexShrink: 0, color: token.colorTextSecondary }} />
              <span style={{ fontSize: 15, color: token.colorText }}>{formatLocationLabel(location, t, true)}</span>
            </button>
          ))}
      </div>
    </div>
  );
};

export default HomeSearchLocationScreen;
